import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { Save, Lock, ShieldCheck } from 'lucide-react';
import platformAdminService from '../../services/platformAdminService';
import PermissionCheckboxGroup from './PermissionCheckboxGroup';
import { describeApiError } from '../../utils/apiError';
import {
  PERMISSIONS,
  PARTNER_PERMISSIONS,
  PARTNER_PERMISSION_LABELS,
} from '../../constants/permissions';
import { LIMITS_COPY } from '../../constants/partnerCopy';
import { platformMcpTooltips, platformMcpUntickWarning } from '../../utils/platformMcpPermission';
import styles from './PlatformAdmin.module.scss';

// Partner admins always keep these so they can't be locked out (§4.4).
const LOCKED_KEYS = [PARTNER_PERMISSIONS.USERS, PARTNER_PERMISSIONS.COMPANIES];

const KEY_WARNINGS = {
  [PERMISSIONS.USAGE_RUN_CONTENT]: LIMITS_COPY.usageRunContentWarning,
  [PARTNER_PERMISSIONS.USAGE_RUN_CONTENT]: LIMITS_COPY.partnerUsageRunContentWarning,
};

const KEY_TOOLTIPS = LOCKED_KEYS.reduce((acc, key) => ({ ...acc, [key]: LIMITS_COPY.lockedAdminKey }), {});

const COMPANY_KEY_TOOLTIPS = platformMcpTooltips('partnerLimits');

// Fallback when the live catalog predates the partner scope.
const STATIC_PARTNER_CATALOG = Object.entries(PARTNER_PERMISSION_LABELS).map(([key, label]) => ({
  key,
  label,
  scope: 'partner',
  category: 'Partner',
}));

/**
 * The partner envelope (Partner tier guide §4.4): which features the partner's
 * companies may use at most, plus which partner powers are on. Saved as ONE
 * declarative list of both kinds. Narrowing it takes effect for every company
 * under the partner on the next request — nothing is rewritten.
 */
const PartnerEntitlementsTab = ({ partner, catalog, notify }) => {
  const [selected, setSelected] = useState([]);
  const [original, setOriginal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const companyCatalog = useMemo(() => (catalog || []).filter((p) => p.scope === 'company'), [catalog]);
  const partnerCatalog = useMemo(() => {
    const live = (catalog || []).filter((p) => p.scope === 'partner');
    return live.length ? live : STATIC_PARTNER_CATALOG;
  }, [catalog]);

  const labelOf = useCallback(
    (key) => [...companyCatalog, ...partnerCatalog].find((p) => p.key === key)?.label || key,
    [companyCatalog, partnerCatalog]
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await platformAdminService.getPartnerEntitlements(partner.id);
      const keys = [
        ...(Array.isArray(data?.company_permission_keys) ? data.company_permission_keys : []),
        ...(Array.isArray(data?.partner_permission_keys) ? data.partner_permission_keys : []),
      ];
      setSelected(keys);
      // Locked keys are kept server-side regardless, so they never count as a change.
      setOriginal(Array.from(new Set([...keys, ...LOCKED_KEYS])));
    } catch (err) {
      notify(describeApiError(err, 'Failed to load feature limits.'), 'error');
    } finally {
      setLoading(false);
    }
  }, [partner.id, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (key) =>
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  // Locked keys are always sent, whatever the stored list says.
  const toSave = useMemo(() => Array.from(new Set([...selected, ...LOCKED_KEYS])), [selected]);

  const diff = useMemo(() => {
    const before = new Set(original);
    const after = new Set(toSave);
    return {
      added: toSave.filter((k) => !before.has(k)),
      removed: original.filter((k) => !after.has(k)),
    };
  }, [original, toSave]);

  const save = async () => {
    try {
      setSaving(true);
      await platformAdminService.setPartnerEntitlements(partner.id, toSave);
      notify(LIMITS_COPY.platformSaved, 'success');
      setConfirmOpen(false);
      // Declarative + last write wins: show what the server now holds.
      await load();
    } catch (err) {
      notify(describeApiError(err, 'Failed to save feature limits.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClick = () => {
    if (diff.removed.length > 0) setConfirmOpen(true);
    else save();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  const nothingChanged = diff.added.length === 0 && diff.removed.length === 0;
  const companyKeyWarnings = { ...KEY_WARNINGS, ...platformMcpUntickWarning(original, selected) };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{LIMITS_COPY.companyGroupTitle}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {LIMITS_COPY.companyGroupHelp} The partner chooses per company within this list.
          </Typography>
        </Box>
        <PermissionCheckboxGroup
          catalog={companyCatalog}
          selected={selected}
          onToggle={toggle}
          keyTooltips={COMPANY_KEY_TOOLTIPS}
          keyWarnings={companyKeyWarnings}
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{LIMITS_COPY.partnerGroupTitle}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>{LIMITS_COPY.partnerGroupHelp}</Typography>
        </Box>
        <PermissionCheckboxGroup
          catalog={partnerCatalog}
          selected={selected}
          onToggle={toggle}
          lockedKeys={LOCKED_KEYS}
          keyTooltips={KEY_TOOLTIPS}
          keyWarnings={KEY_WARNINGS}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5 }}>
        {!nothingChanged && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {diff.added.length} added · {diff.removed.length} removed
          </Typography>
        )}
        <Button
          variant="contained"
          startIcon={<Save size={16} />}
          className={styles.primaryBtn}
          onClick={handleSaveClick}
          disabled={saving || nothingChanged}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Save limits'}
        </Button>
      </Box>

      {/* Removal confirmation with the diff (§4.4) */}
      <Dialog open={confirmOpen} onClose={() => !saving && setConfirmOpen(false)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>Update {partner.name}'s limits?</DialogTitle>
        <DialogContent className={styles.deleteContent}>
          <Box className={styles.warningIconWrap}><Lock /></Box>
          <Typography className={styles.deleteText}>
            Removed features become unavailable to every company under this partner immediately.
          </Typography>
          <Box sx={{ textAlign: 'left', width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {diff.removed.length > 0 && (
              <Typography variant="body2">
                <strong>Removed:</strong> {diff.removed.map(labelOf).join(', ')}
              </Typography>
            )}
            {diff.added.length > 0 && (
              <Typography variant="body2">
                <strong>Added:</strong> {diff.added.map(labelOf).join(', ')}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setConfirmOpen(false)} disabled={saving} className={styles.cancelButton}>Cancel</Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={saving}
            className={styles.primaryBtn}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <ShieldCheck size={16} />}
          >
            Apply changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartnerEntitlementsTab;
